import { api } from '../utils/api';
import {
  DashboardMainResponse,
  TodaysSalesResponse,
  TodayVsLastWeekResponse,
  TodayVs30DaysAvgResponse,
  WeeklySalesResponse,
  AmazonWeeklySalesResponse,
  MonthlySalesResponse,
  SalesComparisonResponse,
  QuarterSalesResponse,
  HighestDaySalesResponse,
  SalesBySKUResponse,
  NewVsReturningCustomerResponse,
  CheckoutAbandonedResponse,
  AvgOrderValueResponse,
  UnitPerTransactionResponse,
  ReturningCustomerResponse,
  SalesTotalSelectedRangeResponse,
  QuarterProjectionResponse,
  HighestWeekResponse,
  BestSellingProductResponse,
  HourlySalesResponse,
  ProjectionSalesResponse,
  ConversionRateResponse,
  CheckoutAbandonmentRateResponse,
  ProjectionGrowthRateResponse,
  HealthCheckResponse,
  MetricsResponse,
} from '../types/api';

class DashboardApiService {
  // System Routes
  async getHealth(): Promise<HealthCheckResponse> {
    const response = await api.get('/api/health');
    return response.data;
  }

  async getMetrics(): Promise<MetricsResponse> {
    const response = await api.get('/api/metrics');
    return response.data;
  }

  // Dashboard Main
  async getDashboard(): Promise<DashboardMainResponse> {
    const response = await api.get('/api/');
    return response.data;
  }

  // Today's Sales Data
  async getSalesToday(): Promise<TodaysSalesResponse> {
    const response = await api.get('/api/get-sales-today');
    return response.data;
  }

  async getTodayVsLastWeek(): Promise<TodayVsLastWeekResponse> {
    const response = await api.get('/api/admin/dashboard/today-vs-lastweek');
    return response.data;
  }

  async getTodayVs30DaysAvg(start?: string, end?: string): Promise<TodayVs30DaysAvgResponse> {
    const url = start && end 
      ? `/api/admin/dashboard/today-vs-30daysAvg/${start}/${end}`
      : '/api/admin/dashboard/today-vs-30daysAvg';
    const response = await api.get(url);
    return response.data;
  }

  // Weekly Sales
  async getThisWeekSales(slug: string = 'week_to_date'): Promise<WeeklySalesResponse> {
    const response = await api.get(`/api/admin/dashboard/week/${slug}`);
    return response.data;
  }

  async getThisWeekSalesAmazon(slug: string = 'week_to_date'): Promise<AmazonWeeklySalesResponse> {
    const response = await api.get(`/api/this-week-amazon/${slug}`);
    return response.data;
  }

  // Monthly Sales
  async getThisMonthSales(slug: string = 'month_to_date'): Promise<MonthlySalesResponse> {
    const response = await api.get(`/api/admin/dashboard/month/${slug}`);
    return response.data;
  }

  async getThisMonthSalesAmazon(slug: string = 'month_to_date'): Promise<MonthlySalesResponse> {
    const response = await api.get(`/api/this-month-amazon/${slug}`);
    return response.data;
  }

  // Sales Comparison
  async getSalesComparison(type: string): Promise<SalesComparisonResponse> {
    const response = await api.get(`/api/admin/dashboard/comparison/${type}`);
    return response.data;
  }

  async getSalesComparisonAmazon(type: string): Promise<SalesComparisonResponse> {
    const response = await api.get(`/api/get-comparison-amazon/${type}`);
    return response.data;
  }

  // Quarter Sales
  async getQuarterSales(slug: string = 'quarter_to_date'): Promise<QuarterSalesResponse> {
    const response = await api.get(`/api/admin/dashboard/quarter/${slug}`);
    return response.data;
  }

  async getQuarterSalesAmazon(slug: string = 'quarter_to_date'): Promise<QuarterSalesResponse> {
    const response = await api.get(`/api/this-quarter-amazon/${slug}`);
    return response.data;
  }

  // Historical Records
  async getHighestDaySalesAllOverTime(): Promise<HighestDaySalesResponse> {
    const response = await api.get('/api/admin/dashboard/highest-daysales-allover-time');
    return response.data;
  }

  async getHighestDaySalesCurrentDayOfWeek(): Promise<any> {
    const response = await api.get('/api/admin/dashboard/high-for-sales-on-current-day-of-the-week');
    return response.data;
  }

  async getCurrentPacingVsPastDays(): Promise<any> {
    const response = await api.get('/api/admin/dashboard/current-pacing-vs-past-days');
    return response.data;
  }

  async getWeekMonthEquivalentPeriodsHistorically(): Promise<any> {
    const response = await api.get('/api/admin/dashboard/week-month-equivalent-periods-historically');
    return response.data;
  }

  async getHighestWeekAllOver(): Promise<HighestWeekResponse> {
    const response = await api.get('/api/highest-week-allover');
    return response.data;
  }

  // Product & SKU Analytics
  async getSalesBySKU(type: 'sku' | 'channel', timeRange: string): Promise<SalesBySKUResponse> {
    const response = await api.get(`/api/admin/dashboard/sales-by-sku/${type}/${timeRange}`);
    return response.data;
  }

  async getSalesBySKUAmazon(type: 'sku' | 'channel', timeRange: string): Promise<SalesBySKUResponse> {
    const response = await api.get(`/api/sales-by-sku-amazon/${type}/${timeRange}`);
    return response.data;
  }

  async getBestSellingProduct(slug: string = 'today'): Promise<BestSellingProductResponse> {
    const response = await api.get(`/api/best-selling-product/${slug}`);
    return response.data;
  }

  // Customer Analytics
  async getNewVsReturningCustomer(slug: 'today' | 'week_to_date' | 'month_to_date' | 'year_to_date'): Promise<NewVsReturningCustomerResponse> {
    const response = await api.get(`/api/admin/dashboard/new-vs-returning-customer/${slug}`);
    return response.data;
  }

  async getNewVsReturningCustomerAmazon(slug: 'today' | 'week_to_date' | 'month_to_date' | 'year_to_date'): Promise<NewVsReturningCustomerResponse> {
    const response = await api.get(`/api/new-vs-returning-customer-amazon/${slug}`);
    return response.data;
  }

  // Advanced Metrics with 30-Day Averages
  async getCheckoutAbandonedWith30DaysAvg(): Promise<CheckoutAbandonedResponse> {
    const response = await api.get('/api/admin/dashboard/checkout-abandoned-with-30days-avg');
    return response.data;
  }

  async getAvgOrderValueWith30DaysAvg(): Promise<AvgOrderValueResponse> {
    const response = await api.get('/api/admin/dashboard/avg-order-value-with-30days-avg');
    return response.data;
  }

  async getUnitPerTransactionWith30DaysAvg(): Promise<UnitPerTransactionResponse> {
    const response = await api.get('/api/admin/dashboard/unit-per-transaction-30days-avg');
    return response.data;
  }

  async getReturningCustomerWith30DaysAvg(): Promise<ReturningCustomerResponse> {
    const response = await api.get('/api/admin/dashboard/returning-cust-with-30days-avg');
    return response.data;
  }

  // Custom Date Range
  async getSalesTotalForSelectedRange(start: string, end: string): Promise<SalesTotalSelectedRangeResponse> {
    const response = await api.get(`/api/admin/dashboard/sales-total-for-selectedRange/${start}/${end}`);
    return response.data;
  }

  async getSalesTotalForSelectedRangeAmazon(start: string, end: string): Promise<SalesTotalSelectedRangeResponse> {
    const response = await api.get(`/api/sales-total-for-selectedRange-amazon/${start}/${end}`);
    return response.data;
  }

  // Projections
  async getQuarterProjection(): Promise<QuarterProjectionResponse> {
    const response = await api.get('/api/quarter-projection');
    return response.data;
  }

  async getAmazonQuarterProjection(): Promise<QuarterProjectionResponse> {
    const response = await api.get('/api/amazon-quarter-projection');
    return response.data;
  }

  async getProjectionSales(): Promise<ProjectionSalesResponse> {
    const response = await api.get('/api/projection-sales');
    return response.data;
  }

  async getProjectionSalesData(): Promise<any> {
    const response = await api.get('/api/projection-sales-data');
    return response.data;
  }

  async getAmazonProjectionGrowthRate(): Promise<ProjectionGrowthRateResponse> {
    const response = await api.get('/api/amazon-projection-growth-rate');
    return response.data;
  }

  // Hourly Breakdown
  async getTodayHourlyRepresentation(slug: 'today' | 'last_week' | 'current_year' | 'last_year' = 'today'): Promise<HourlySalesResponse> {
    const response = await api.get(`/api/today-shourly-representation/${slug}`);
    return response.data;
  }

  async getTodayHourlySalesAmazon(slug: 'today' | 'last_week' | 'current_year' | 'last_year' = 'today'): Promise<HourlySalesResponse> {
    const response = await api.get(`/api/today-hourly-sales-amazon/${slug}`);
    return response.data;
  }

  // Conversion Rates
  async getAmazonConversionRate(slug: string = 'today'): Promise<ConversionRateResponse> {
    const response = await api.get(`/api/amazon-conversion-rate/${slug}`);
    return response.data;
  }

  async getShopifyConversionRate(slug: string = 'today'): Promise<ConversionRateResponse> {
    const response = await api.get(`/api/shopify-conversion-rate/${slug}`);
    return response.data;
  }

  async getAmazonCheckoutAbandonment(): Promise<CheckoutAbandonmentRateResponse> {
    const response = await api.get('/api/amazon-checkout-abandonment');
    return response.data;
  }

  // Comprehensive fetch - gets all dashboard data
  async getAllDashboardData(): Promise<any> {
    try {
      // Fetch all data in parallel where possible
      const [
        dashboard,
        salesToday,
        todayVsLastWeek,
        weekSalesShopify,
        weekSalesAmazon,
        salesBySKUShopify,
        salesBySKUAmazon,
        newVsReturningToday,
        hourlySalesShopify,
        hourlySalesAmazon,
        quarterProjectionShopify,
        quarterProjectionAmazon,
      ] = await Promise.allSettled([
        this.getDashboard(),
        this.getSalesToday(),
        this.getTodayVsLastWeek(),
        this.getThisWeekSales('week_to_date'),
        this.getThisWeekSalesAmazon('week_to_date'),
        this.getSalesBySKU('sku', 'daily'),
        this.getSalesBySKUAmazon('sku', 'daily'),
        this.getNewVsReturningCustomer('today'),
        this.getTodayHourlyRepresentation('today'),
        this.getTodayHourlySalesAmazon('today'),
        this.getQuarterProjection(),
        this.getAmazonQuarterProjection(),
      ]);

      return {
        dashboard: dashboard.status === 'fulfilled' ? dashboard.value : null,
        salesToday: salesToday.status === 'fulfilled' ? salesToday.value : null,
        todayVsLastWeek: todayVsLastWeek.status === 'fulfilled' ? todayVsLastWeek.value : null,
        weekSales: weekSalesShopify.status === 'fulfilled' ? weekSalesShopify.value : null,
        weekSalesAmazon: weekSalesAmazon.status === 'fulfilled' ? weekSalesAmazon.value : null,
        salesBySKU: salesBySKUShopify.status === 'fulfilled' ? salesBySKUShopify.value : null,
        salesBySKUAmazon: salesBySKUAmazon.status === 'fulfilled' ? salesBySKUAmazon.value : null,
        newVsReturningCustomers: newVsReturningToday.status === 'fulfilled' ? newVsReturningToday.value : null,
        hourlySales: hourlySalesShopify.status === 'fulfilled' ? hourlySalesShopify.value : null,
        hourlySalesAmazon: hourlySalesAmazon.status === 'fulfilled' ? hourlySalesAmazon.value : null,
        quarterProjection: quarterProjectionShopify.status === 'fulfilled' ? quarterProjectionShopify.value : null,
        quarterProjectionAmazon: quarterProjectionAmazon.status === 'fulfilled' ? quarterProjectionAmazon.value : null,
      };
    } catch (error) {
      console.error('Error fetching comprehensive dashboard data:', error);
      throw error;
    }
  }
}

export const dashboardApi = new DashboardApiService();

